import { HoverHintTrigger } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";

import {
  COMMISSION_RUN_APPROVAL_SEGMENTS,
  COMMISSION_RUN_WORKFLOW_MAIN_STEPS,
  commissionRunApprovalAt,
  commissionRunWorkflowMainStepIndex,
  isCommissionRunWorkflowMainStepComplete,
  isCommissionRunWorkflowMainStepCurrent,
  type CommissionRunApprovalRole,
  type CommissionRunWorkflowFields,
} from "./commissionRunWorkflow";

function formatWorkflowTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function mainStepChipClass(complete: boolean, current: boolean): string {
  return cn(
    "rounded-full border px-3 py-1 text-xs font-medium",
    complete
      ? "border-sky-700 bg-sky-700 text-white"
      : "border-slate-300 bg-white text-slate-600",
    current && !complete ? "ring-2 ring-sky-300 ring-offset-1" : "",
  );
}

function approvalBadgeClass(approved: boolean): string {
  return cn(
    "rounded-full border px-3 py-1 text-xs font-medium",
    approved
      ? "border-sky-700 bg-sky-100 text-sky-950"
      : "border-slate-200 bg-slate-50 text-slate-400",
  );
}

function approvedStepSegments(run: CommissionRunWorkflowFields) {
  return (
    <span className="inline-flex overflow-hidden rounded-full border border-slate-300 bg-white">
      {COMMISSION_RUN_APPROVAL_SEGMENTS.map((segment, index) => {
        const approved = commissionRunApprovalAt(run, segment.role) != null;
        return (
          <span
            key={segment.id}
            className={cn(
              "px-2 py-1 text-xs font-medium",
              index > 0 ? "border-l border-slate-300" : "",
              approved ? "bg-sky-700 text-white" : "text-slate-500",
            )}
          >
            {segment.label}
          </span>
        );
      })}
    </span>
  );
}

export type CommissionRunWorkflowStatusProps = {
  run: CommissionRunWorkflowFields;
  onApproveRole?: (role: CommissionRunApprovalRole) => void;
  approveRolePending?: boolean;
};

export function CommissionRunWorkflowStatus({
  run,
  onApproveRole,
  approveRolePending = false,
}: CommissionRunWorkflowStatusProps) {
  const activeIndex = commissionRunWorkflowMainStepIndex(run);

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white px-5 py-4 shadow-sm">
      <ol className="flex flex-wrap items-center gap-y-2">
        {COMMISSION_RUN_WORKFLOW_MAIN_STEPS.map((step, index) => {
          const complete = isCommissionRunWorkflowMainStepComplete(run, index);
          const current = isCommissionRunWorkflowMainStepCurrent(run, index);
          const connectorLit = index < activeIndex;

          if (step.id === "approved") {
            return (
              <li key={step.id} className="flex items-center">
                {index > 0 ? (
                  <span
                    className={cn("mx-1 h-px w-5", connectorLit ? "bg-sky-400" : "bg-slate-300")}
                    aria-hidden
                  />
                ) : null}
                <HoverHintTrigger
                  trigger={
                    <span
                      className={cn(
                        mainStepChipClass(complete, current),
                        "inline-flex items-center gap-1.5",
                      )}
                    >
                      <span>{step.label}</span>
                      {approvedStepSegments(run)}
                    </span>
                  }
                >
                  {COMMISSION_RUN_APPROVAL_SEGMENTS.map((segment) => {
                    const at = commissionRunApprovalAt(run, segment.role);
                    const byKey =
                      segment.role === "inside_sales"
                        ? run.approved_inside_sales_by
                        : segment.role === "operations"
                          ? run.approved_operations_by
                          : run.approved_executive_by;
                    return (
                      <p key={segment.id} className="mb-1 last:mb-0">
                        {segment.badgeLabel}:{" "}
                        {at != null
                          ? `Approved ${formatWorkflowTimestamp(at)}${byKey != null ? ` by ${byKey}` : ""}`
                          : "Not approved yet."}
                      </p>
                    );
                  })}
                </HoverHintTrigger>
              </li>
            );
          }

          return (
            <li key={step.id} className="flex items-center">
              {index > 0 ? (
                <span
                  className={cn("mx-1 h-px w-5", connectorLit ? "bg-sky-400" : "bg-slate-300")}
                  aria-hidden
                />
              ) : null}
              <HoverHintTrigger
                trigger={
                  <span className={mainStepChipClass(complete, current)}>{step.label}</span>
                }
              >
                {complete ? `${step.label} complete.` : current ? `${step.label} in progress.` : "Not reached yet."}
              </HoverHintTrigger>
            </li>
          );
        })}
      </ol>
      <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
        {COMMISSION_RUN_APPROVAL_SEGMENTS.map((segment) => {
          const at = commissionRunApprovalAt(run, segment.role);
          const approved = at != null;
          const canRecord =
            onApproveRole != null && run.locked_at != null && !approved && !approveRolePending;
          return (
            <HoverHintTrigger
              key={segment.role}
              trigger={
                canRecord ? (
                  <button
                    type="button"
                    className={approvalBadgeClass(approved)}
                    onClick={() => onApproveRole(segment.role)}
                  >
                    {segment.badgeLabel}
                  </button>
                ) : (
                  <span className={approvalBadgeClass(approved)}>{segment.badgeLabel}</span>
                )
              }
            >
              {approved
                ? `Approved ${formatWorkflowTimestamp(at!)}`
                : canRecord
                  ? `Record ${segment.badgeLabel} sign-off.`
                  : `${segment.badgeLabel} sign-off not recorded yet.`}
            </HoverHintTrigger>
          );
        })}
      </div>
    </div>
  );
}
